import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';
import Pagination from '../../../components/common/Pagination';
import {
  getServices,
  createService,
  updateService,
  deleteService,
} from '../../../services/servicesService';
import { getApiErrorMessage } from '../../../services/http';

const Services = () => {
  // Create form
  const [name, setName] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // List
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const filtered = services.filter(s => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
  });

  const exportToCSV = () => {
    try {
      const cols = [
        { header: 'Name', accessor: r => r.name },
        { header: 'Default Price', accessor: r => r.defaultPrice },
        { header: 'Description', accessor: r => r.description },
      ];
      const header = cols.map(c => '"' + c.header + '"').join(',');
      const body = filtered.map(r => cols.map(c => '"' + (c.accessor(r) ?? '') + '"').join(',')).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export failed', e);
      toast.error('Export failed');
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getServices({
        search: search || undefined,
        page: currentPage,
        limit: 20
      });
      setServices(data?.services || data || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (e) {
      console.error('Load services failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to load services'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [currentPage]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      loadServices();
    }, 400);
    return () => clearTimeout(debounce);
  }, [search]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !defaultPrice) {
      toast.error('Name and Default Price are required');
      return;
    }
    try {
      setCreating(true);
      await createService({
        name: name.trim(),
        defaultPrice: Number(defaultPrice),
        description: description.trim() || undefined,
      });
      toast.success('Service created');
      setName('');
      setDefaultPrice('');
      setDescription('');
      loadServices();
    } catch (e) {
      console.error('Create service failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to create'));
    } finally {
      setCreating(false);
    }
  };

  const onEdit = (svc) => {
    setEditId(svc._id);
    setEditName(svc.name || '');
    setEditPrice(String(svc.defaultPrice ?? ''));
    setEditDesc(svc.description || '');
  };

  const onSaveEdit = async () => {
    if (!editId) return;
    if (!editName.trim() || editPrice === '') {
      toast.error('Name and Default Price are required');
      return;
    }
    try {
      setSavingEdit(true);
      await updateService(editId, {
        name: editName.trim(),
        defaultPrice: Number(editPrice),
        description: editDesc.trim() || undefined,
      });
      toast.success('Service updated');
      setEditId(null);
      loadServices();
    } catch (e) {
      console.error('Update service failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to update'));
    } finally {
      setSavingEdit(false);
    }
  };

  const onCancelEdit = () => {
    setEditId(null);
  };

  const onDeactivate = async (id) => {
    try {
      await deleteService(id);
      toast.success('Service deactivated');
      loadServices();
    } catch (e) {
      console.error('Deactivate service failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to deactivate'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/dashboard" />
        <h2 className="text-2xl font-bold text-gray-900">Services</h2>
      </div>

      {/* Create Service */}
      <form onSubmit={onCreate} className="card p-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-900">New Service</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input className="input-field" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Business Card" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Price</label>
            <input type="number" step="0.01" className="input-field" value={defaultPrice} onChange={e => setDefaultPrice(e.target.value)} placeholder="0.00" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input className="input-field" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional details" />
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Saving...' : 'Add Service'}</button>
      </form>

      {/* Toolbar */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 max-w-md">
            <input className="input-field" placeholder="Search services" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn-secondary ml-4" onClick={exportToCSV}>Export CSV</button>
        </div>
      </div>

      {/* Services List */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50">
        <div className="p-6">
          <h3 className="text-lg font-bold text-purple-900 mb-4">All Services</h3>
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-purple-100">
              <thead className="bg-gradient-to-r from-purple-50 to-fuchsia-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-purple-800 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-purple-800 uppercase tracking-wider">Default Price</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-purple-800 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-purple-800 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((svc, index) => (
                  <tr key={svc._id} className={`hover:bg-purple-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {editId === svc._id ? (
                      <input className="input-field" value={editName} onChange={e => setEditName(e.target.value)} />
                    ) : (
                      svc.name
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {editId === svc._id ? (
                      <input type="number" step="0.01" className="input-field" value={editPrice} onChange={e => setEditPrice(e.target.value)} />
                    ) : (
                      `KSh ${Number(svc.defaultPrice || 0).toFixed(2)}`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {editId === svc._id ? (
                      <input className="input-field" value={editDesc} onChange={e => setEditDesc(e.target.value)} />
                    ) : (
                      svc.description || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    {editId === svc._id ? (
                      <div className="flex justify-end gap-2">
                        <button className="btn-primary" onClick={onSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</button>
                        <button className="btn-secondary" onClick={onCancelEdit}>Cancel</button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <button className="btn-secondary" onClick={() => onEdit(svc)}>Edit</button>
                        <button className="btn-danger" onClick={() => onDeactivate(svc._id)}>Deactivate</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && filtered.length === 0 && <div className="text-center text-gray-500 py-6">No services</div>}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        total={total}
        pageSize={20}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default Services; 